// Read-outs (Talk's replies, Listen) play through ONE audio element, unlocked in a tap. iOS Safari plays audio only
// from an element that was first played inside a user gesture: an `new Audio(url).play()` after an awaited fetch is
// refused, silently unless someone checks (Kareem, 4 Nov: Talk never spoke back on his iPhone). So the tap that starts
// talking (or Listen, or Replay) calls unlockAudio() synchronously, before anything is awaited: it plays a tenth of a
// second of silence on the shared element and resumes an AudioContext, and every reply after that reuses the element.
// If a play is still refused, playAudio says so ('refused') and the caller shows "Tap to hear the reply".

/** What became of a read-out. */
export type PlayResult = 'ended' | 'refused' | 'stopped' | 'failed';

let el: HTMLAudioElement | null = null;
let silent: string | null = null;
let ctx: AudioContext | null = null;
let pending: ((r: PlayResult) => void) | null = null;

/** A tenth of a second of silence as a WAV blob (a blob: URL, which the Content-Security-Policy's media-src allows). */
function silentUrl(): string {
  if (silent) return silent;
  const rate = 8000, n = rate / 10, b = new DataView(new ArrayBuffer(44 + n * 2));
  const str = (o: number, s: string) => { for (let i = 0; i < s.length; i++) b.setUint8(o + i, s.charCodeAt(i)); };
  str(0, 'RIFF'); b.setUint32(4, 36 + n * 2, true); str(8, 'WAVE'); str(12, 'fmt '); b.setUint32(16, 16, true); b.setUint16(20, 1, true);
  b.setUint16(22, 1, true); b.setUint32(24, rate, true); b.setUint32(28, rate * 2, true); b.setUint16(32, 2, true); b.setUint16(34, 16, true);
  str(36, 'data'); b.setUint32(40, n * 2, true);
  silent = URL.createObjectURL(new Blob([b.buffer], { type: 'audio/wav' }));
  return silent;
}

/** The shared element (made on first use). */
export function audioElement(): HTMLAudioElement {
  if (!el) {
    el = new Audio();
    el.preload = 'auto';
    el.setAttribute('playsinline', '');
    el.dataset.dpwReadout = '1';
  }
  return el;
}

/**
 * iOS 17 and later: say the app both records and plays, so a reply is heard while the microphone is open (and is
 * not ducked to the earpiece level). Ignored where navigator.audioSession does not exist.
 */
export function playAndRecord() {
  const s = (navigator as Navigator & { audioSession?: { type: string } }).audioSession;
  try { if (s && s.type !== 'play-and-record') s.type = 'play-and-record'; } catch { /* not supported here */ }
}

/**
 * Call SYNCHRONOUSLY inside a tap handler (before any await): plays silence on the shared element and resumes the
 * shared AudioContext, so later read-outs may play without a tap. Safe to call on every tap.
 */
export function unlockAudio(): HTMLAudioElement {
  playAndRecord();
  const a = audioElement();
  if (!pending && a.paused) {
    a.src = silentUrl();
    a.play().catch(() => undefined);
  }
  resumeAudioContext();
  return a;
}

/**
 * Call SYNCHRONOUSLY inside a tap handler: makes (once) and resumes the shared AudioContext that measures the
 * microphone's level. One made after an await (getUserMedia) starts suspended on iOS and hears nothing, so the
 * silence that ends a turn would never be seen. Returns it, or null without Web Audio.
 */
export function resumeAudioContext(): AudioContext | null {
  try {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    if (!ctx || ctx.state === 'closed') ctx = new AC();
    if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
    return ctx;
  } catch { return null; }
}

/** Stop whatever is being read out; its playAudio resolves 'stopped'. */
export function stopAudio() {
  const a = el;
  if (a && !a.paused) a.pause();
  const p = pending;
  pending = null;
  p?.('stopped');
}

/**
 * Play a read-out on the shared element and wait for it to end. 'refused': the browser would not play without a
 * tap (call unlockAudio in the next tap, then play again). Never throws.
 */
export function playAudio(url: string): Promise<PlayResult> {
  stopAudio();
  const a = audioElement();
  return new Promise<PlayResult>(resolve => {
    const done = (r: PlayResult) => { if (pending === done) pending = null; a.onended = null; a.onerror = null; resolve(r); };
    pending = done;
    a.onended = () => { if (pending === done) done('ended'); };
    a.onerror = () => { if (pending === done) done('failed'); };
    a.src = url;
    a.currentTime = 0;
    a.play().catch((e: unknown) => {
      if (pending !== done) return; // stopped or replaced meanwhile
      const name = e instanceof DOMException ? e.name : '';
      done(name === 'NotAllowedError' ? 'refused' : name === 'AbortError' ? 'stopped' : 'failed');
    });
  });
}
